import type { FC, PropsWithChildren, ReactNode } from "react";

import { styles } from "@repo/helpers";
import * as React from "react";
import { useCallback, useContext, useState } from "react";

import style from "./style.module.scss";

type ExpandBreakpoint = "sm" | "md" | "lg" | "xl" | "xxl";

interface NavbarContextValue {
	expanded: boolean;
	toggle: () => void;
	collapseOnSelect: boolean;
}

const NavbarContext = React.createContext<NavbarContextValue | null>(null);

export interface NavbarProps {
	brand?: ReactNode;
	brandHref?: string;
	onBrandClick?: () => void;
	className?: string;
	children?: ReactNode;
	bg?: "light" | "dark";
	variant?: "light" | "dark";
	expand?: boolean | ExpandBreakpoint;
	sticky?: "top" | "bottom";
	fixed?: "top" | "bottom";
	/** Controlled expanded state; if provided, component is controlled */
	expanded?: boolean;
	/** Uncontrolled initial expanded state */
	defaultExpanded?: boolean;
	/** Called when expanded state changes */
	onToggle?: (expanded: boolean) => void;
	/** Collapse automatically when a nav item is selected */
	collapseOnSelect?: boolean;
}

export function Navbar({
	brand,
	brandHref,
	onBrandClick,
	className,
	children,
	bg = "light",
	variant = "light",
	expand = "lg",
	sticky,
	fixed,
	expanded,
	defaultExpanded = false,
	onToggle,
	collapseOnSelect = false,
}: NavbarProps) {
	const isControlled = expanded !== undefined;
	const [uncontrolledExpanded, setUncontrolledExpanded] = useState(defaultExpanded);
	const actualExpanded = isControlled ? !!expanded : uncontrolledExpanded;

	const handleBrandClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
		if (onBrandClick) {
			e?.preventDefault();
			onBrandClick();
		}
	};

	const handleToggle = useCallback(() => {
		const next = !actualExpanded;
		if (!isControlled) setUncontrolledExpanded(next);
		onToggle?.(next);
	}, [actualExpanded, isControlled, onToggle]);

	const expandClass =
		expand === false
			? ""
			: expand === true
				? style.expand
				: (style as any)[`expand${String(expand).toLowerCase()}`];

	const navbarClassName = styles(
		style.navbar,
		bg === "dark" && style.bgDark,
		variant === "dark" && style.variantDark,
		sticky && style.sticky,
		fixed === "top" && style.fixedTop,
		fixed === "bottom" && style.fixedBottom,
		expandClass,
		className,
	);

	const ctx: NavbarContextValue = {
		expanded: actualExpanded,
		toggle: handleToggle,
		collapseOnSelect,
	};

	return (
		<NavbarContext.Provider value={ctx}>
			<nav className={navbarClassName}>
				{brand && (
					<NavbarBrand href={brandHref || "#"} onClick={handleBrandClick}>
						{brand}
					</NavbarBrand>
				)}
				{children}
			</nav>
		</NavbarContext.Provider>
	);
}

export interface NavbarNavProps extends PropsWithChildren {
	className?: string;
	align?: "left" | "right";
	navbarScroll?: boolean;
}

export function NavbarNav({ children, className, align = "left", navbarScroll }: NavbarNavProps) {
	return (
		<div
			className={styles(
				style.nav,
				align === "right" && style.right,
				navbarScroll && (style as any).navbarScroll,
				className,
			)}
		>
			{children}
		</div>
	);
}

export interface NavbarItemProps {
	children: ReactNode;
	active?: boolean;
	onClick?: () => void;
	href?: string;
	className?: string;
}

export function NavbarItem({ children, active, onClick, href, className }: NavbarItemProps) {
	const Tag = href ? "a" : "button";
	const navbar = useContext(NavbarContext);

	const handleClick = useCallback(
		(e: React.MouseEvent<any>) => {
			onClick?.();
			if (navbar && navbar.collapseOnSelect && navbar.expanded) {
				navbar.toggle();
			}
		},
		[onClick, navbar],
	);

	const props: any = {
		className: styles(style.navItem, active && style.navItemActive, className),
		onClick: handleClick,
	};

	if (href) {
		props.href = href;
	} else {
		props.type = "button";
	}

	return <Tag {...props}>{children}</Tag>;
}

// Alias matching React-Bootstrap's Navbar.Link semantics
export type NavbarLinkProps = NavbarItemProps;

export function NavbarLink(props: NavbarLinkProps) {
	return <NavbarItem {...props} />;
}

// Simple Nav container (alias of NavbarNav for compatibility with existing code)
export interface NavProps extends PropsWithChildren {
	className?: string;
}

export function Nav({ children, className }: NavProps) {
	return <div className={styles(style.nav, className)}>{children}</div>;
}

export interface NavbarTextProps extends PropsWithChildren {
	className?: string;
	onClick?: (e: React.MouseEvent<HTMLSpanElement>) => void;
}

export function NavbarText({ children, className, onClick }: NavbarTextProps) {
	const s = style as any;
	return (
		<span className={styles(s.navbarText, className)} onClick={onClick}>
			{children}
		</span>
	);
}

export interface NavbarBrandProps extends PropsWithChildren {
	className?: string;
	href?: string;
	onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

export function NavbarBrand({ children, className, href, onClick }: NavbarBrandProps) {
	const Tag = href ? "a" : "div";
	const props: any = {
		className: styles(style.brand, className),
		onClick,
	};
	if (href) props.href = href;

	return <Tag {...props}>{children}</Tag>;
}

export interface NavDropdownProps extends PropsWithChildren {
	id?: string;
	title: ReactNode;
	className?: string;
}

export interface NavDropdownItemProps {
	children: ReactNode;
	onClick?: () => void;
	disabled?: boolean;
}

function NavDropdownItem({ children, onClick, disabled }: NavDropdownItemProps) {
	const s = style as any;
	return (
		<button
			type="button"
			className={styles(s.navDropdownItem, disabled && s.navDropdownItemDisabled)}
			onClick={disabled ? undefined : onClick}
			disabled={disabled}
		>
			{children}
		</button>
	);
}

interface NavDropdownComponent extends FC<NavDropdownProps> {
	Item: typeof NavDropdownItem;
}

export const NavDropdown: NavDropdownComponent = ({ id, title, className, children }: NavDropdownProps) => {
	const [open, setOpen] = useState(false);

	const toggle = useCallback(() => setOpen((v) => !v), []);

	const s = style as any;
	return (
		<div className={styles(s.navDropdown, className)} id={id}>
			<button type="button" className={s.navDropdownToggle} onClick={toggle} aria-expanded={open}>
				<span>{title}</span>
			</button>
			{open && <div className={s.navDropdownMenu}>{children}</div>}
		</div>
	);
};

NavDropdown.Item = NavDropdownItem;
